import { useState } from 'react';
import { AxiosError } from 'axios';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { Dropdown } from 'components/common/dropdown';
import { Code, FormGroup } from 'components/common/form';

interface Props {
  createAchievement?: boolean;
  updateData: () => Promise<void> | void;
}

const AchievementImportJson = ({
  createAchievement = false,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, theme } = useReduxState();

  const [error, setError] = useState<string | undefined>(undefined);
  const [warning, setWarning] = useState<string | undefined>(undefined);

  const handleHide = () => {
    setError(undefined);
    setWarning(undefined);
  };

  const handleSubmit = async (values: { json: string }) => {
    try {
      const result = await api.post('/admin/achievements/import-json', {
        ...JSON.parse(values.json),
        createAchievement
      });

      dispatch.addNotice({
        text: messages.achievementImportJsonSuccess,
        type: 'success'
      });

      await updateData();

      setError(undefined);
      if (result.data?.invalidCompletions?.length) {
        setWarning(JSON.stringify(result.data.invalidCompletions));
        return false;
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status !== 500) {
        setError(JSON.stringify(error.response?.data));
      } else {
        setError(
          JSON.stringify({
            error:
              error instanceof AxiosError
                ? error.response?.data
                : error.toString()
          })
        );
        dispatch.handleErrorDispatch(error);
      }
      return false;
    }

    return true;
  };

  const getErrorMessage = (message: string) => {
    try {
      return JSON.stringify(JSON.parse(message), null, 2);
    } catch {
      return message || 'unknown_error';
    }
  };

  return (
    <div>
      <Dropdown
        buttonStyle={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        category="success"
        icon="chevron-down"
        size={createAchievement ? 'medium' : 'header'}
        styleContentButtons={false}
      >
        <ModalButtonWithForm
          buttonIcon="code"
          buttonIsDropdown={true}
          buttonSize="header"
          buttonText={messages.coursesImportJson}
          modalActionText={messages.commonImport}
          modalTitle={messages.coursesImportJson}
          modalWidth="medium"
          onHide={handleHide}
          onSubmit={handleSubmit}
        >
          {() => (
            <FormGroup htmlFor="json" label={messages.commonData}>
              <Code
                field="json"
                id="json-data"
                validateJSON={true}
                style={{
                  ...(error || warning
                    ? {
                        border: `1px solid ${
                          error
                            ? theme.dangerColor
                            : theme.warningStandaloneColor
                        }`
                      }
                    : {})
                }}
              />

              {error || warning ? (
                <div className="container">
                  <div className={`label ${error ? 'error' : 'warning'}`}>
                    {error ? 'Error' : messages.usersImportInvalid}
                  </div>

                  <pre>
                    <code>{getErrorMessage(error || warning)}</code>
                  </pre>
                </div>
              ) : null}
            </FormGroup>
          )}
        </ModalButtonWithForm>
      </Dropdown>

      <style jsx>{`
        .container {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-top: 10px;
        }
        .label {
          font-size: 1.3rem;
          font-weight: 600;
        }
        .error {
          color: ${theme.dangerColor};
        }
        .warning {
          color: ${theme.warningStandaloneColor};
        }
      `}</style>
    </div>
  );
};

export default AchievementImportJson;
